import { useEffect, useMemo, useState } from "react";
import { SideNav } from "./SideNav";
import { TopNav } from "./TopNav";
import { ensureMaterialSymbolsLoaded } from "./materialSymbols";
import { themeStyle } from "./theme";
import type { DashboardShellProps } from "./types";

const DEFAULT_DARK_MODE_STORAGE_KEY = "inAppDashboardDarkMode";

function readStoredDarkMode(storageKey: string) {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(storageKey) === "true";
}

function defaultNavigate(route: string) {
  if (!route || typeof window === "undefined") return;
  window.location.assign(route);
}

export function DashboardShell({
  children,
  config,
  navItems,
  account,
  alertCount,
  darkMode,
  defaultDarkMode: _defaultDarkMode = false,
  darkModeStorageKey = DEFAULT_DARK_MODE_STORAGE_KEY,
  onDarkModeChange,
  onNavigate = defaultNavigate,
  onSearch,
  onAlertsClick,
  leftAccessory,
  rightAccessory,
  className = "",
  style,
  bodyDarkModeClassName,
}: DashboardShellProps) {
  const [expanded, setExpanded] = useState(false);
  const [storedDarkMode, setStoredDarkMode] = useState(() => readStoredDarkMode(darkModeStorageKey));
  const resolvedDarkMode = darkMode ?? storedDarkMode;
  const resolvedStyle = useMemo(
    () => ({ ...themeStyle(config.theme, resolvedDarkMode), ...style }),
    [config.theme, resolvedDarkMode, style],
  );
  const hasChildren = children !== undefined && children !== null;

  useEffect(() => {
    ensureMaterialSymbolsLoaded();
  }, []);

  useEffect(() => {
    if (darkMode !== undefined || typeof window === "undefined") return;
    window.localStorage.setItem(darkModeStorageKey, String(storedDarkMode));
  }, [darkMode, darkModeStorageKey, storedDarkMode]);

  useEffect(() => {
    if (!bodyDarkModeClassName || typeof document === "undefined") return;
    document.body.classList.toggle(bodyDarkModeClassName, resolvedDarkMode);
    return () => document.body.classList.remove(bodyDarkModeClassName);
  }, [bodyDarkModeClassName, resolvedDarkMode]);

  const handleToggleDarkMode = useMemo(
    () => () => {
      const nextMode = !resolvedDarkMode;
      if (darkMode === undefined) setStoredDarkMode(nextMode);
      onDarkModeChange?.(nextMode);
    },
    [darkMode, onDarkModeChange, resolvedDarkMode],
  );

  const handleNavigate = useMemo(
    () => (route: string) => {
      if (!route) return;
      setExpanded(false);
      if (typeof window !== "undefined" && /^https?:\/\//i.test(route)) {
        window.location.assign(route);
        return;
      }
      onNavigate(route);
    },
    [onNavigate],
  );

  const shellClasses = [
    "in-app-shell",
    !hasChildren ? "shell-only" : "",
    expanded ? "side-expanded" : "",
    resolvedDarkMode ? "dark" : "",
    resolvedDarkMode ? "is-dark" : "is-light",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={shellClasses} data-theme={resolvedDarkMode ? "dark" : "light"} style={resolvedStyle}>
      <TopNav
        config={config}
        expanded={expanded}
        account={account}
        alertCount={alertCount}
        darkMode={resolvedDarkMode}
        onToggleDarkMode={handleToggleDarkMode}
        onToggleNav={() => setExpanded((value) => !value)}
        onNavigate={handleNavigate}
        onSearch={onSearch}
        onAlertsClick={onAlertsClick}
      />

      {hasChildren ? (
        <div className="in-app-body">
          <SideNav navItems={navItems} onNavigate={handleNavigate} />
          <main className="in-app-main">{children}</main>
        </div>
      ) : (
        <SideNav navItems={navItems} onNavigate={handleNavigate} />
      )}

      {(leftAccessory || rightAccessory) && (
        <div className="in-app-page-toolbar">
          <div className="in-app-page-toolbar__left">{leftAccessory}</div>
          <div className="in-app-page-toolbar__right">{rightAccessory}</div>
        </div>
      )}
    </div>
  );
}
